import React, { useState, useEffect, useContext } from 'react';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import DateFnsUtils from '@date-io/moment';
import { CSVLink } from 'react-csv';
import { useTranslation, Trans } from 'react-i18next';
import FormControl from '@material-ui/core/FormControl';
import Tooltip from '@material-ui/core/Tooltip';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';

// icon
import GetAppIcon from '@material-ui/icons/GetApp';
import { StyledFormButton } from '../../../../components/button/Button';

// Provider
import ModeContext from '../../../../context/ModeContext';
import UserContext from '../../../../context/UserContext';

// component
import DamModal from '../../../../components/modal/Modal';
import DamLegend from '../../legend/DamLegend';
import DamLargeLegend from '../../legend/DamLargeLegend';
import DamLargeDataListsRid from './DamLargeDataListsRid';
import areas from '../../../../assets/json/region';

import { globalStyle } from '../../../../global/style';

// export config
const headers = [
  { label: <Trans>อ่างเก็บน้ำ</Trans>, key: 'dam_name' },
  { label: <Trans>จังหวัด</Trans>, key: 'province_name' },
  // { label: <Trans>ภาค</Trans>, key: 'geocode.area_name.th' },
  { label: <Trans>ลุ่มน้ำ</Trans>, key: 'basin_name' },
  { label: <Trans>ความจุอ่างที่ รนส. (ล้าน ลบ.ม.)</Trans>, key: 'max_storage' },
  { label: <Trans>ความจุอ่างที่ รนก. (ล้าน ลบ.ม.)</Trans>, key: 'normal_storage' },
  { label: <Trans>ความจุอ่างที่ น้ำใช้การ (ล้าน ลบ.ม.)</Trans>, key: 'uses_water' },
  { label: <Trans>น้ำในอ่าง กักเก็บ (ล้าน ลบ.ม.)</Trans>, key: 'dam_storage' },
  { label: <Trans>น้ำในอ่าง กักเก็บ (% ความจุ)</Trans>, key: 'dam_storage_percent' },
  { label: <Trans>น้ำในอ่าง ปริมาตรใช้การ (ล้าน ลบ.ม.)</Trans>, key: 'dam_uses_water' },
  { label: <Trans>น้ำในอ่าง ปริมาตรใช้การ (% ความจุ)</Trans>, key: 'dam_uses_water_percent' },
  { label: <Trans>ปริมาณน้ำไหลลงอ่าง ค่าเฉลี่ยรวมทั้งปี (ล้าน ลบ.ม.)</Trans>, key: 'dam_inflow_avg' },
  { label: <Trans>ปริมาณน้ำไหลลงอ่าง ปริมาตรน้ำ (ล้าน ลบ.ม.)</Trans>, key: 'dam_inflow' },
  { label: <Trans>ปริมาณน้ำไหลลงอ่าง สะสมตั้งแต่ 1 ม.ค. (ล้าน ลบ.ม.)</Trans>, key: 'dam_inflow_acc' },
  { label: <Trans>ปริมาณน้ำไหลลงอ่าง สะสมตั้งแต่ 1 ม.ค. (% ความจุ)</Trans>, key: 'dam_inflow_acc_percent' },
  { label: <Trans>ปริมาณน้ำระบาย ปริมาตรน้ำ (ล้าน ลบ.ม.)</Trans>, key: 'dam_released' },
  { label: <Trans>ปริมาณน้ำระบาย สะสมตั้งแต่ 1 ม.ค. (ล้าน ลบ.ม.)</Trans>, key: 'dam_released_acc' },
];
const datas = [];

// set total summary
let grandTotalInflow = 0;
let grandTotalInflowAcc = 0;
let grandTotalInflowAvg = 0;
let grandTotalReleased = 0;
let grandTotalReleasedAcc = 0;
let grandTotalDamStorage = 0;
let grandTotalDamUseWater = 0;
let grandTotalMaxStorage = 0;
let grandTotalNormalStorage = 0;
let grandTotalUsesWater = 0;

export default function DamLargeListsRidContainer({ useRuleCurve }) {
  const { t } = useTranslation();
  const [datasLargeDamRid, setLargeDamDataRid] = useState([]);
  const [selectedDateRid, setSelectedDateRid] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [error, setError] = useState('');

  const [csv, setCsv] = useState({
    headers: [],
    data: [],
  });

  const useStyles = makeStyles(globalStyle);
  const classes = useStyles();

  // modal
  const [modalData, setModalData] = useState({
    open: false,
    header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
    subheader: null,
    content: null,
  });

  // get context
  const { currentMode, currentArea } = useContext(ModeContext);
  const { userIsLogin } = useContext(UserContext);

  function filterData(currentData, mode, area) {
    let filterD = [];

    if (mode === 'province' && area.code !== '0') {
      filterD = currentData.filter((d) => d.geocode.province_code === area.code);
    } else if (currentMode === 'basin' && area.code !== '0') {
      filterD = currentData.filter((d) => d.basin.basin_code === parseInt(area.code, 10));
    } else {
      filterD = currentData;
    }

    return filterD;
  }

  const fetchData = async (d) => {
    let url = `${process.env.REACT_APP_API_URL}/analyst/dam?dam_size=1&dam_date=${d}`;
    if (d === undefined) {
      url = `${process.env.REACT_APP_API_URL}/analyst/dam?dam_size=1`;
    }

    fetch(url)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Something went wrong ...');
      })
      .then((result) => {
        /*
        Add filter by province or basin later
        */
        const datasDamDailyRid = orderBy(result.data.dam_daily, ['dam_storage_percent'], ['desc']);
        // const datasDamDailyRid = filterData(orderDatas, currentMode, currentArea);

        // custom render export data
        if (areas.length === 7) {
          // remove อื่นๆ
          areas.splice(-1, 1);
        }

        areas.map((area) => {
          datas.push({
            dam_name: area.name,
          });

          // summary value by region
          let totalInflow = 0;
          let totalInflowAcc = 0;
          let totalInflowAvg = 0;
          let totalReleased = 0;
          let totalReleasedAcc = 0;
          let totalDamStorage = 0;
          let totalDamUseWater = 0;
          let totalMaxStorage = 0;
          let totalNormalStorage = 0;
          let totalUsesWater = 0;

          datasDamDailyRid
            .filter((row) => row.geocode.area_code === area.id.toString() && row.agency.id === 12)
            .map((row) => {
              totalInflow += row.dam_inflow;
              totalInflowAcc += row.dam_inflow_acc;
              totalInflowAvg += row.dam_inflow_avg;
              totalReleased += row.dam_released;
              totalReleasedAcc += row.dam_released_acc;
              totalMaxStorage += row.dam.max_storage;
              totalNormalStorage += row.dam.normal_storage;
              totalUsesWater += row.dam.uses_water;
              totalDamStorage += row.dam_storage;
              totalDamUseWater += row.dam_uses_water;

              grandTotalInflow += row.dam_inflow;
              grandTotalInflowAvg += row.dam_inflow_avg;
              grandTotalInflowAcc += row.dam_inflow_acc;
              grandTotalReleased += row.dam_released;
              grandTotalReleasedAcc += row.dam_released_acc;
              grandTotalDamStorage += row.dam_storage;
              grandTotalDamUseWater += row.dam_uses_water;
              grandTotalMaxStorage += row.dam.max_storage;
              grandTotalNormalStorage += row.dam.normal_storage;
              grandTotalUsesWater += row.dam.uses_water;

              datas.push({
                dam_name: row.dam.dam_name.th,
                province_name: row.geocode.province_name.th,
                basin_name: row.basin.basin_name.th,
                max_storage: row.dam.max_storage,
                normal_storage: row.dam.normal_storage,
                uses_water: row.dam.uses_water,
                dam_storage: row.dam_storage,
                dam_storage_percent: row.dam_storage_percent,
                dam_uses_water: row.dam_uses_water,
                dam_uses_water_percent: row.dam_uses_water_percent,
                dam_inflow_avg: row.dam_inflow_avg,
                dam_inflow: row.dam_inflow,
                dam_inflow_acc: row.dam_inflow_acc,
                dam_inflow_acc_percent: row.dam_inflow_acc_percent,
                dam_released: row.dam_released,
                dam_released_acc: row.dam_released_acc,
              });

              return null;
            });

          // push summary data for each region
          const regionDamStoragePercent = !Number.isNaN(totalMaxStorage, 10) ? ((totalDamStorage * 100) / totalMaxStorage).toFixed(0) : '-';
          const regionDamUsesWaterPercent = !Number.isNaN(totalMaxStorage, 10) ? ((totalDamUseWater * 100) / totalMaxStorage).toFixed(0) : '-';
          const regionDamInflowAccPercent = ((100 * totalInflowAcc) / totalInflowAvg).toFixed(2);

          datas.push({
            dam_name: t(`รวม${area.name}`),
            max_storage: totalMaxStorage,
            normal_storage: totalNormalStorage,
            uses_water: totalUsesWater,
            dam_storage: totalDamStorage,
            dam_storage_percent: regionDamStoragePercent,
            dam_uses_water: totalDamUseWater,
            dam_uses_water_percent: regionDamUsesWaterPercent,
            dam_inflow_avg: totalInflowAvg,
            dam_inflow: totalInflow,
            dam_inflow_acc: totalInflowAcc,
            dam_inflow_acc_percent: regionDamInflowAccPercent,
            dam_released: totalReleased,
            dam_released_acc: totalReleasedAcc,
          });

          return null;
        });

        // grand total
        const allTotalMaxStorage = !Number.isNaN(grandTotalMaxStorage, 10) ? grandTotalMaxStorage.toFixed(0) : '-';
        const allDamStoragePercent = !Number.isNaN(grandTotalMaxStorage, 10) ? ((grandTotalDamStorage * 100) / grandTotalMaxStorage).toFixed(0) : '-';
        const allDamUsesWaterPercent = !Number.isNaN(grandTotalMaxStorage, 10) ? ((grandTotalDamUseWater * 100) / grandTotalMaxStorage).toFixed(0) : '-';
        const allDamInflowAccPercent = ((100 * grandTotalInflowAcc) / grandTotalInflowAvg)
          .toFixed(2);
        const allTotalDamUseWater = !Number.isNaN(grandTotalUsesWater, 10) ? grandTotalUsesWater.toFixed(0) : '-';
        datas.push({
          dam_name: t('รวมทั้งประเทศ'),
          max_storage: allTotalMaxStorage,
          normal_storage: grandTotalNormalStorage,
          uses_water: allTotalDamUseWater,
          dam_storage: grandTotalDamStorage,
          dam_storage_percent: allDamStoragePercent,
          dam_uses_water: grandTotalDamUseWater,
          dam_uses_water_percent: allDamUsesWaterPercent,
          dam_inflow_avg: grandTotalInflowAvg,
          dam_inflow: grandTotalInflow,
          dam_inflow_acc: grandTotalInflowAcc,
          dam_inflow_acc_percent: allDamInflowAccPercent,
          dam_released: grandTotalReleased,
          dam_released_acc: grandTotalReleasedAcc,
        });

        // set data state
        setLargeDamDataRid(datasDamDailyRid);
        setSelectedDateRid(result.data.dam_daily[0].dam_date);
        if (!d) setMaxDate(result.data.dam_daily[0].dam_date);
        setCsv({
          headers,
          data: datas,
        });
      })
      .catch((err) => setError(err));
  };

  const handleModalChanage = (d) => {
    setModalData(d);
  };

  const handleDateChange = (d) => {
    const myDate = moment(d).format('YYYY-MM-DD');
    setSelectedDateRid(myDate);

    fetchData(myDate);

    handleModalChanage({
      open: false,
      header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
      subheader: null,
      content: null,
    });
  };

  // fetch data
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {error && <div>Error Occured</div>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={9}>
          <div style={{ paddingTop: '35px' }}>
            <Typography variant="h5" component="h5">
              {`${t('ข้อมูลวันที่')} ${selectedDateRid ? moment(selectedDateRid).locale('th').format('DD MMM YYYY') : ''}`}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={8} sm={4} md={2}>
          {/* Search and Download */}
          <FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                margin="normal"
                variant="inline"
                format="YYYY-MM-DD"
                id="date-picker"
                label={t('ข้อมูลวันที่')}
                value={selectedDateRid || moment().subtract(1, 'days').format('YYYY-MM-DD')}
                maxDate={maxDate}
                onChange={handleDateChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </MuiPickersUtilsProvider>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={2} md={1}>
          {userIsLogin && (
            <div style={{ paddingTop: '35px' }}>
              <CSVLink id="download_csv" data={csv.data} headers={csv.headers} filename={`dam_large_rid_${selectedDateRid}.csv`}>
                <Tooltip title={t('ดาวน์โหลดไฟล์ อ่างเก็บน้ำ')} placement="top">
                  <StyledFormButton
                    variant="outlined"
                    size="small"
                    className={classes.button}
                    startIcon={<GetAppIcon />}
                  >
                    {t('ดาวน์โหลด')}
                  </StyledFormButton>
                </Tooltip>
              </CSVLink>
            </div>
          )}
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <Box>
            <DamLargeDataListsRid
              datas={filterData(datasLargeDamRid, currentMode, currentArea)}
              latestDate={selectedDateRid}
              handleModalChanage={handleModalChanage}
              useRuleCurve={useRuleCurve}
            />
          </Box>
          <Box mt={2}>
            {useRuleCurve ? <DamLargeLegend /> : <DamLegend />}
          </Box>
          <Box mt={2}>
            <Typography variant="subtitle1" display="block" fontSize="15px">{t('คำย่อ')}</Typography>
            <Typography variant="body2" display="block" fontSize="12px">{t('รนส. หมายถึง ระดับน้ำสูงสุด')}</Typography>
            <Typography variant="body2" display="block" fontSize="12px">{t('รนก. หมายถึง ระดับน้ำกับเก็บ')}</Typography>
          </Box>
          <Box mt={2} align="right">
            {t('ข้อมูลจากกรมชลประทาน')}
          </Box>
        </Grid>
      </Grid>

      <DamModal
        open={modalData.open}
        modalHeader={modalData.header}
        modalSubHeader={modalData.subheader}
        modalContent={modalData.content}
      />
    </>
  );
}

DamLargeListsRidContainer.propTypes = {
  useRuleCurve: PropTypes.instanceOf(Boolean).isRequired,
};
